import React, { useState, useLayoutEffect, useCallback, useId } from 'react';
import AnchoredTourCallout from './AnchoredTourCallout';
import './BrowseMapOnboarding.css';

const PADDING = 8;

function expandRect(rect) {
  if (!rect || rect.width <= 0 || rect.height <= 0) return null;
  return {
    left: Math.max(0, rect.left - PADDING),
    top: Math.max(0, rect.top - PADDING),
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  };
}

/**
 * Single spotlight on “Add a New Place” + anchored callout (no Joyride, no buttons).
 */
export default function AddPlaceOnboarding({ visible, buttonRef }) {
  const maskUid = useId().replace(/:/g, '');
  const maskHref = `url(#add-place-onboarding-mask-${maskUid})`;
  const [hole, setHole] = useState(null);
  const [layoutTick, setLayoutTick] = useState(0);

  const measure = useCallback(() => {
    if (!visible || !buttonRef?.current) return;
    const r = expandRect(buttonRef.current.getBoundingClientRect());
    setHole(r);
    setLayoutTick((n) => n + 1);
  }, [visible, buttonRef]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useLayoutEffect(() => {
    if (!visible) return undefined;
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    const id = window.setInterval(measure, 400);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      window.clearInterval(id);
    };
  }, [visible, measure]);

  const w = typeof window !== 'undefined' ? window.innerWidth : 0;
  const h = typeof window !== 'undefined' ? window.innerHeight : 0;
  const btn = buttonRef?.current ?? null;
  const ready = Boolean(visible && hole && btn && w && h);

  if (!visible || !w || !h) return null;

  return (
    <>
      {hole && (
        <div className="browse-onboarding-root" aria-hidden={ready ? 'true' : undefined}>
          <svg className="browse-onboarding-svg" width={w} height={h}>
            <defs>
              <mask id={`add-place-onboarding-mask-${maskUid}`} maskUnits="userSpaceOnUse">
                <rect x={0} y={0} width={w} height={h} fill="white" />
                <rect
                  x={hole.left}
                  y={hole.top}
                  width={hole.width}
                  height={hole.height}
                  rx={6}
                  ry={6}
                  fill="black"
                />
              </mask>
            </defs>
            <rect x={0} y={0} width={w} height={h} fill="rgba(0, 0, 0, 0.4)" mask={maskHref} />
          </svg>
        </div>
      )}

      {ready && (
        <div className="browse-onboarding-callouts" role="region" aria-label="Add a place tip">
          <AnchoredTourCallout
            show
            reference={btn}
            placement="bottom"
            fallbackPlacements={['top', 'right', 'left']}
            layoutTick={layoutTick}
          >
            <p className="tour-callout__title">Add your story</p>
            <p className="tour-callout__body">
              When you are ready, click Add a New Place to contribute a story to the map.
            </p>
          </AnchoredTourCallout>
        </div>
      )}
    </>
  );
}
